import React, { useCallback, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

// styles
import Styled from './FlightResultCard.styled';
import Kaaba from '@/assets/images/kaaba.svg';

// hooks
import { useTranslation } from 'react-i18next';
import { useQuery } from '@/hooks/useQuery';
import useIsInViewport from '@/hooks/useIsInViewport';
import usePushCardView from '@/hooks/usePushCardView';
import { useMultiVariant } from '@tajawal/web-common/lib';

// types
import { LegModel } from '@tajawal/web-flights-sdk/lib/types/leg.types';
import { ItineraryModel } from '@tajawal/web-flights-sdk/lib/types/itinerary.types';
import type Sort from '@tajawal/web-flights-sdk/lib/stores/sort.store';
import type FlightInfoStore from '@tajawal/web-flights-sdk/lib/stores/flight-info.store';

// components
import { Icons } from '@tajawal/ct-web-design-system';
import LegInfo from '@/app-desktop/components/LegInfo';
import Warning from '@/app-desktop/components/Warning';
import SortTags from '@/app-desktop/components/FlightResultCard/components/SortTags';
import FlightCardPricing from '@/app-desktop/components/FlightResultCard/components/FlightCardPricing';
import Image from '@/app-common/components/Image';

// utils
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _size from 'lodash/size';
import _isFunction from 'lodash/isFunction';
import { generateGroupingResultCardTestIDs, getSearchResultsLegTestIds } from '@/utils/test-ids.utils';
import { getBaggageIcon, getCabinContent } from '@/utils/content.utils';

// constants
import { APP_PAGES } from '@tajawal/web-core-sdk-v2/lib/constants';
import { AB_TESTING_FLAGS } from '@/constants/analytics.constants';

const FareFamilies = dynamic(() => import('@/app-desktop/containers/FareFamilies'), { ssr: false });

export type FlightResultCardProps = {
  onSelectItinerary: (itinerary: ItineraryModel) => void;
  sort?: Sort;
  itinerary?: ItineraryModel;
  groupInfo: { index: number; size: number };
  isTopPick?: boolean;
  isInbound?: boolean;
  onOpenFlightDetails: (itinerary: ItineraryModel) => void;
  onSelectFareFamilyItinerary?: (id: string) => void;
  currentPage?: string;
  $resources?: FlightInfoStore['$resources'];
  index: number;
  isInGroup?: boolean;
  isExpanded?: boolean;
  setIsExpanded?: (isExpanded: boolean) => void;
  onCheckPricesClick?: () => void;
  isPaxPage?: boolean;
  isValidToShowFareFamilies?: boolean;
};

function FlightResultCard({
  sort,
  itinerary,
  groupInfo,
  isTopPick,
  isInbound,
  onSelectItinerary,
  onOpenFlightDetails,
  onSelectFareFamilyItinerary,
  $resources,
  index,
  currentPage = APP_PAGES.FLIGHTS_RESULTS,
  onCheckPricesClick,
  isInGroup = false,
  isPaxPage = false,
  isValidToShowFareFamilies = true,
}: FlightResultCardProps): JSX.Element {
  const { t } = useTranslation();
  const { isRoundTrip, isDomestic } = useQuery();
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInViewport(cardRef);
  const { isMultiCity } = useQuery();

  const isGroup = groupInfo?.size > 1;

  const {
    hasMixedCabin = false,
    includedCabinClass = '',
    fareFamilies = [],
    id,
    hasAirportChange,
    baggageInfo,
    hasFareFamilies,
    isHajjUmrahFlight,
    direction,
  } = itinerary || {};
  const includedCabin = getCabinContent({ cabin: includedCabinClass, t });
  const _hasFareFamilies = hasFareFamilies || _size(fareFamilies) > 0;
  const { isCheapest, isShortest, isRecommended } = sort || {};
  const [isFareFamiliesShown, setIsFareFamiliesShown] = useState(false);
  const showWarning = hasMixedCabin || hasAirportChange || isHajjUmrahFlight;

  const { isReady } = useMultiVariant(AB_TESTING_FLAGS.SEATS_REMAINING);

  const testIds = generateGroupingResultCardTestIDs({
    groupIndex: groupInfo?.index,
    direction: '',
    optionNo: 100,
    isTopPick,
    isGroupOption: groupInfo?.size === 0,
    isDesktop: true,
  });

  const baggageIcon = useCallback(
    (index) =>
      getBaggageIcon({
        index,
        baggageInfo,
        fareFamiliesBaggage: _hasFareFamilies,
      }),
    []
  );

  function constructTags() {
    const tags = [];
    if (_isEmpty(sort)) {
      return tags;
    }
    if (isRecommended(id)) {
      tags.push('isRecommended');
    }
    if (isCheapest(id)) {
      tags.push('isCheapest');
    }
    if (isShortest(id)) {
      tags.push('isShortest');
    }
    return tags;
  }

  const selectedTags = constructTags();

  function handleCheckPricesClick() {
    if (_isFunction(onCheckPricesClick)) {
      onCheckPricesClick();
      return;
    }

    if (_hasFareFamilies) {
      setIsFareFamiliesShown(!isFareFamiliesShown);
      onSelectFareFamilyItinerary(id);
      if (!isFareFamiliesShown && cardRef.current) {
        cardRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
  usePushCardView({
    itinerary,
    groupInfo,
    isGroup,
    isInView,
    currentPage,
    $resources,
    isSeatsRemainingReady: isReady,
  });

  return (
    <Styled.FlightCardWrapper data-testid={testIds.card} ref={cardRef}>
      <Styled.SearchCard $isInGroup={isInGroup || isFareFamiliesShown}>
        <Styled.SearchCardContent
          $hasWarning={showWarning}
          $hasSortedTags={selectedTags.length > 0}
          $isRoundTrip={isRoundTrip && !isDomestic}
          $isMultiCity={isMultiCity}
        >
          {selectedTags.length > 0 && (
            <Styled.SortedTags $isRoundTrip={isRoundTrip && !isDomestic}>
              <SortTags index={index} selectedTags={selectedTags} />
            </Styled.SortedTags>
          )}
          <Styled.LegWrapper
            $haveMixedCabin={showWarning}
            $hasSortedTags={selectedTags.length > 0}
            $isRoundTrip={isRoundTrip && !isDomestic}
          >
            {_map(itinerary?.legs, (leg: LegModel, index: number) => {
              const testIds = getSearchResultsLegTestIds(
                groupInfo?.index,
                index,
                false,
                isTopPick,
                true,
                isDomestic,
                direction
              );
              return (
                <LegInfo
                  baggageInfo={baggageInfo}
                  testIds={testIds}
                  index={index}
                  baggageIcon={baggageIcon(index)}
                  key={`leg-${leg.id}`}
                  leg={leg}
                  hasFareFamilies={_hasFareFamilies}
                />
              );
            })}
          </Styled.LegWrapper>
          {showWarning && (
            <Styled.Messages>
              {hasMixedCabin && (
                <Warning
                  icon={<Icons.CabinClass width="16" height="16" />}
                  testId={testIds.mixedCabinWarning}
                  message={t('FlightCard.IncludedCabin', { includedCabin })}
                />
              )}
              {hasAirportChange && (
                <Warning
                  icon={<Icons.ArrowsLeftRight width="16" height="16" />}
                  testId={testIds.airportChangeWarning}
                  message={t('FlightCard.AirportChangeWarning')}
                />
              )}
              {isHajjUmrahFlight && (
                <Warning
                  icon={<Image height="16px" width="16px" src={Kaaba} alt="Hajj logo" />}
                  testId={testIds.hajjAndUmrah}
                  message={t('FlightDetails.FilightConditions_HajjUmrah_Title')}
                />
              )}
            </Styled.Messages>
          )}
        </Styled.SearchCardContent>
        <FlightCardPricing
          isInbound={isInbound}
          hasFareFamilies={_hasFareFamilies}
          onSelectItinerary={onSelectItinerary}
          itinerary={itinerary}
          onCheckPricesClick={handleCheckPricesClick}
          onOpenFlightDetails={onOpenFlightDetails}
          index={index}
          isFareFamiliesShown={isFareFamiliesShown}
          testIds={testIds}
          $resources={$resources}
          isPaxPage={isPaxPage}
        />
      </Styled.SearchCard>
      {isValidToShowFareFamilies && _hasFareFamilies && isFareFamiliesShown && !isPaxPage && (
        <Styled.FareFamilies $isInGroup={isInGroup && isFareFamiliesShown && !isDomestic}>
          <FareFamilies cardPosition={index} selectedFareFamilyItinerary={itinerary} />
        </Styled.FareFamilies>
      )}
    </Styled.FlightCardWrapper>
  );
}

export default FlightResultCard;
